/**
 * Document Processing Utilities (Frontend)
 * 
 * This module provides file type detection and formatting utilities.
 * Actual document processing is done on the backend.
 */

export type SupportedFileType = 'pdf' | 'docx' | 'txt' | 'xlsx' | 'csv' | 'image';

/**
 * Detect file type from file name
 */
export function getFileType(fileName: string): SupportedFileType | null {
  const ext = fileName.split('.').pop()?.toLowerCase();
  
  switch (ext) {
    case 'pdf':
      return 'pdf';
    case 'docx':
    case 'doc':
      return 'docx';
    case 'txt':
      return 'txt';
    case 'xlsx':
    case 'xls':
      return 'xlsx';
    case 'csv':
      return 'csv';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
      return 'image';
    default:
      return null;
  }
}

/**
 * Get file icon name based on type
 */
export function getFileIcon(type: SupportedFileType): string {
  switch (type) {
    case 'pdf':
      return 'FileText';
    case 'docx':
      return 'FileText';
    case 'txt':
      return 'File';
    case 'xlsx':
    case 'csv':
      return 'FileSpreadsheet';
    case 'image':
      return 'Image';
    default:
      return 'File';
  }
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Check if file type is supported
 */
export function isSupported(fileName: string): boolean {
  return getFileType(fileName) !== null;
}

/**
 * Get accepted file extensions for input
 */
export function getAcceptedExtensions(): string {
  return '.pdf,.docx,.doc,.txt,.xlsx,.xls,.csv,.jpg,.jpeg,.png';
}
